import { db } from './firebase';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import { updateKnowledgeBaseAPI, queryKnowledgeBaseAPI } from './neuralKnowledgeBase';
import type { NeuralKBEnrichment } from './neuralKnowledgeBase';

export interface OrchestratorStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  tool: string;
  command?: string;
  output?: string;
  error?: string;
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  metadata?: Record<string, any>;
}

export interface OrchestratorSession {
  id: string;
  target: string;
  goal: string;
  status: 'planning' | 'executing' | 'completed' | 'failed';
  steps: OrchestratorStep[];
  currentStep: number;
  plan: string;
  findings: any[];
  vulnerabilities: any[];
  createdAt: Date;
  updatedAt: Date;
  userId?: string;
  sessionData?: Record<string, any>;
}

export interface AI4EHTool {
  name: string;
  description: string;
  category: 'reconnaissance' | 'vulnerability_assessment' | 'exploitation' | 'post_exploitation';
  command: string;
  parameters: string[];
  outputFormat: 'text' | 'json' | 'xml';
}

class PentestOrchestratorService {
  private availableTools: AI4EHTool[] = [
    {
      name: 'subfinder',
      description: 'Subdomain discovery tool',
      category: 'reconnaissance',
      command: 'subfinder',
      parameters: ['-d', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'httpx',
      description: 'HTTP probe tool',
      category: 'reconnaissance',
      command: 'httpx',
      parameters: ['-u', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'nuclei',
      description: 'Vulnerability scanner',
      category: 'vulnerability_assessment',
      command: 'nuclei',
      parameters: ['-u', '{target}'],
      outputFormat: 'json'
    },
    {
      name: 'naabu',
      description: 'Port scanner',
      category: 'reconnaissance',
      command: 'naabu',
      parameters: ['-host', '{target}'],
      outputFormat: 'json'
    },
    {
      name: 'amass',
      description: 'Subdomain enumeration',
      category: 'reconnaissance',
      command: 'amass',
      parameters: ['enum', '-d', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'nmap',
      description: 'Network scanner',
      category: 'reconnaissance',
      command: 'nmap',
      parameters: ['-p', '80,443,22,21,25,53,110,143,993,995', '-oX', '-', '{target}'],
      outputFormat: 'xml'
    },
    {
      name: 'dirsearch',
      description: 'Directory and file discovery',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['tools/dirsearch/dirsearch.py', '-u', '{target}'],
      outputFormat: 'json'
    },
    {
      name: 'llm_screenshot_classifier',
      description: 'AI-powered screenshot analysis',
      category: 'vulnerability_assessment',
      command: 'python',
      parameters: ['ai4eh/workspace/llm_screenshot_classifier.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'cai_custom_xss_tool',
      description: 'XSS vulnerability testing',
      category: 'vulnerability_assessment',
      command: 'python',
      parameters: ['ai4eh/workspace/cai_custom_xss_tool.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'scrape_content',
      description: 'Multi-page content scraping with sensitive data detection',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/scrape.py', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'extract_keywords',
      description: 'Keyword extraction and content analysis',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/nlp.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'ffuf_fuzz',
      description: 'Directory and file fuzzing',
      category: 'reconnaissance',
      command: 'ffuf',
      parameters: ['-u', '{target}/FUZZ', '-w', 'generated_wordlist.txt'],
      outputFormat: 'json'
    },
    {
      name: 'recollapse_fuzz',
      description: 'HTTP request smuggling detection',
      category: 'vulnerability_assessment',
      command: 'python',
      parameters: ['ai4eh/workspace/recollapse.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'scrape_payloads',
      description: 'Fetch attack payloads from trusted sources',
      category: 'exploitation',
      command: 'python',
      parameters: ['ai4eh/workspace/scrape.py', '--payloads', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'google_dorking',
      description: 'Google dorking for exposed assets',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/google_dorking.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'github_dorking',
      description: 'GitHub dorking for leaked secrets',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/github_dorking.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'asn_lookup',
      description: 'ASN and domain information lookup',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/whois_tool.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'crtsh_lookup',
      description: 'Certificate transparency lookup',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/crtsh_lookup.py', '--target', '{target}'],
      outputFormat: 'text'
    },
    {
      name: 'censys_mapping',
      description: 'Censys mapping for open ports and services',
      category: 'reconnaissance',
      command: 'python',
      parameters: ['ai4eh/workspace/censys_mapping.py', '--target', '{target}'],
      outputFormat: 'text'
    }
  ];

  async createSession(target: string, goal: string, userId?: string): Promise<OrchestratorSession> {
    const session: Omit<OrchestratorSession, 'id'> = {
      target,
      goal,
      status: 'planning',
      steps: [],
      currentStep: 0,
      plan: '',
      findings: [] as any[],
      vulnerabilities: [] as any[],
      createdAt: new Date(),
      updatedAt: new Date(),
      userId,
      sessionData: {}
    };

    // Filter out undefined values before saving to Firestore
    const cleanSession = Object.fromEntries(
      Object.entries(session).filter(([_, value]) => value !== undefined)
    );

    const docRef = await addDoc(collection(db, 'orchestrator-sessions'), {
      ...cleanSession,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { id: docRef.id, ...session };
  }

  async updateSession(sessionId: string, updates: Partial<OrchestratorSession>): Promise<void> {
    const docRef = doc(db, 'orchestrator-sessions', sessionId);
    
    // Filter out undefined values to prevent Firestore errors
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, value]) => value !== undefined)
    );
    
    await updateDoc(docRef, {
      ...cleanUpdates,
      updatedAt: serverTimestamp()
    });
  }

  async getSession(sessionId: string): Promise<OrchestratorSession | null> {
    const docRef = doc(db, 'orchestrator-sessions', sessionId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as OrchestratorSession;
    }
    
    return null;
  }

  async getUserSessions(userId: string): Promise<OrchestratorSession[]> {
    const q = query(
      collection(db, 'orchestrator-sessions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as OrchestratorSession;
    });
  }

  async generatePlan(target: string, goal: string): Promise<string> {
    try {
      // Try to use Gemini for intelligent planning
      const geminiPlan = await this.generateGeminiPlan(target, goal);
      return geminiPlan;
    } catch (error) {
      console.warn('Gemini plan generation failed, falling back to template:', error);
      // Fallback to template plan
      return this.generateTemplatePlan(target, goal);
    }
  }

  private async generateGeminiPlan(target: string, goal: string): Promise<string> {
    // In browser environment, we'll call the backend API instead of directly calling Gemini
    try {
      const response = await fetch('/api/orchestrator/generate-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target,
          goal
        })
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Plan generation failed');
      }

      return result.plan;
    } catch (error) {
      console.error('Error calling plan generation API:', error);
      throw error;
    }
  }

  private generateTemplatePlan(target: string, goal: string): string {
    return `# Pentest Orchestration Plan for ${target}

## Goal: ${goal}

## Phase 1: Reconnaissance
1. **Subdomain Discovery** - Use subfinder to discover subdomains
2. **HTTP Probing** - Use httpx to find live web servers
3. **Port Scanning** - Use nmap for port enumeration

## Phase 2: Vulnerability Assessment
4. **Nuclei Scanning** - Scan for common vulnerabilities
5. **Screenshot Analysis** - Analyze screenshots for high-value targets
6. **XSS Testing** - Test for cross-site scripting vulnerabilities

## Phase 3: Exploitation
7. **Manual Verification** - Verify findings manually
8. **Proof of Concept** - Develop PoCs for critical findings
9. **Report Generation** - Compile comprehensive report

This plan is designed to systematically identify and validate vulnerabilities that could lead to the specified goal.`;
  }

  async executeStep(sessionId: string, step: Omit<OrchestratorStep, 'startTime'>, previousOutput?: any): Promise<OrchestratorStep> {
    console.log(`🔄 Starting execution of step: ${step.tool}`);
    
    const fullStep: OrchestratorStep = {
      ...step,
      startTime: new Date()
    };

    // Update session with new step
    const session = await this.getSession(sessionId);
    if (!session) {
      throw new Error('Session not found');
    }

    // Sanitize the step object to remove undefined values
    const sanitizedStep = Object.fromEntries(
      Object.entries(fullStep).filter(([_, value]) => value !== undefined)
    ) as OrchestratorStep;

    const updatedSteps = [...session.steps, sanitizedStep];
    await this.updateSession(sessionId, { steps: updatedSteps });

    try {
      let result;
      // LLM-powered steps
      if (fullStep.tool === 'generate_wordlist') {
        // Get keywords from previous step (extract_keywords)
        const keywords = previousOutput?.keywords || [];
        const res = await fetch('/api/orchestrator/generate-payloads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ context: { keywords }, type: 'wordlist' })
        });
        const { result: wordlist } = await res.json();
        // Save wordlist to file for ffuf (call backend endpoint or implement here)
        await fetch('/api/orchestrator/save-wordlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wordlist })
        });
        result = { output: wordlist.join('\n'), metadata: { wordlist } };
      } else if (fullStep.tool === 'generate_payloads') {
        // Get context from previous step (e.g., endpoint, parameter, details)
        const context = previousOutput?.context || {};
        const res = await fetch('/api/orchestrator/generate-payloads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ context, type: 'payloads' })
        });
        const { result: payloads } = await res.json();
        result = { output: payloads.join('\n'), metadata: { payloads } };
      } else {
        // Default: execute tool as before
        result = await this.executeTool(fullStep.tool, session.target, fullStep.command);
      }
      
      const completedStep: OrchestratorStep = {
        ...fullStep,
        status: 'completed',
        endTime: new Date(),
        duration: fullStep.startTime ? new Date().getTime() - fullStep.startTime.getTime() : 0,
        output: result.output,
        metadata: result.metadata
      };

      // Sanitize the completed step
      const sanitizedCompletedStep = Object.fromEntries(
        Object.entries(completedStep).filter(([_, value]) => value !== undefined)
      ) as OrchestratorStep;

      // Update step in session
      const updatedSession = await this.getSession(sessionId);
      if (updatedSession) {
        const newSteps = updatedSession.steps.map(s => 
          s.id === step.id ? sanitizedCompletedStep : s
        );
        await this.updateSession(sessionId, { steps: newSteps });
      }

      console.log(`✅ Step completed successfully: ${fullStep.tool}`);
      return completedStep;
    } catch (error) {
      console.error(`❌ Step execution failed: ${fullStep.tool}`, error);
      
      const failedStep: OrchestratorStep = {
        ...fullStep,
        status: 'failed',
        endTime: new Date(),
        duration: fullStep.startTime ? new Date().getTime() - fullStep.startTime.getTime() : 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };

      // Sanitize the failed step
      const sanitizedFailedStep = Object.fromEntries(
        Object.entries(failedStep).filter(([_, value]) => value !== undefined)
      ) as OrchestratorStep;

      // Update step in session
      const updatedSession = await this.getSession(sessionId);
      if (updatedSession) {
        const newSteps = updatedSession.steps.map(s => 
          s.id === step.id ? sanitizedFailedStep : s
        );
        await this.updateSession(sessionId, { steps: newSteps });
      }

      throw error;
    }
  }

  private async executeTool(toolName: string, target: string, customCommand?: string): Promise<{ output: string; metadata: Record<string, any> }> {
    console.log(`🔧 Executing tool: ${toolName} with target: ${target}`);
    
    const tool = this.availableTools.find((t: AI4EHTool) => t.name === toolName);
    if (!tool) {
      throw new Error(`Tool ${toolName} not found`);
    }

    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`Tool execution timed out after 5 minutes`)), 300000); // 5 minutes
    });

    // Create the actual API call promise
    const apiCallPromise = fetch('/api/orchestrator/execute-tool', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tool: toolName,
        target,
        parameters: customCommand ? [customCommand] : tool.parameters
      })
    });

    try {
      // Race between the API call and timeout
      const response = await Promise.race([apiCallPromise, timeoutPromise]);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Tool execution failed');
      }

      console.log(`✅ Tool ${toolName} executed successfully`);

      // Handle different output formats
      let output = '';
      if (result.raw_output) {
        output = result.raw_output;
      } else if (result.data) {
        if (Array.isArray(result.data)) {
          output = result.data.join('\n');
        } else if (typeof result.data === 'string') {
          output = result.data;
        } else {
          output = JSON.stringify(result.data, null, 2);
        }
      } else {
        output = `Tool ${toolName} executed successfully on ${target}`;
      }

      return {
        output,
        metadata: Object.fromEntries(
          Object.entries({
            tool: toolName,
            target,
            timestamp: new Date().toISOString(),
            command: customCommand || `${tool.command} ${tool.parameters.join(' ')}`,
            exitCode: result.exitCode,
            duration: result.duration,
            stderr: result.stderr,
            raw_output: result.raw_output
          }).filter(([_, value]) => value !== undefined)
        )
      };
    } catch (error) {
      console.error(`❌ Tool execution failed: ${toolName}`, error);
      throw error;
    }
  }

  async analyzeFindings(sessionId: string): Promise<{ findings: any[]; vulnerabilities: any[] }> {
    const session = await this.getSession(sessionId);
    if (!session) {
      throw new Error('Session not found');
    }

    const findings: any[] = [];
    const vulnerabilities: any[] = [];

    // Analyze step outputs for findings and vulnerabilities
    session.steps.forEach(step => {
      if (step.output) {
        // Extract findings from output
        if (step.output.includes('Found')) {
          findings.push(step.output);
        }

        // Extract vulnerabilities from nuclei output
        if (step.tool === 'nuclei' && step.output.includes('[')) {
          const vulnMatches = step.output.match(/\[(critical|high|medium|low)\]\s+([^\n]+)/g);
          vulnMatches?.forEach(match => {
            const severityMatch = match.match(/\[(critical|high|medium|low)\]/);
            const descriptionMatch = match.match(/\]\s+(.+)/);
            
            if (severityMatch && descriptionMatch) {
              const vulnerability = {
                title: descriptionMatch[1].split(' on ')[0],
                severity: severityMatch[1] as 'critical' | 'high' | 'medium' | 'low',
                description: descriptionMatch[1],
                evidence: step.output,
                url: descriptionMatch[1].includes(' on ') ? descriptionMatch[1].split(' on ')[1] : undefined
              };

              // Filter out undefined values from vulnerability object
              const cleanVulnerability = Object.fromEntries(
                Object.entries(vulnerability).filter(([_, value]) => value !== undefined)
              ) as OrchestratorSession['vulnerabilities'][0];

              vulnerabilities.push(cleanVulnerability);
            }
          });
        }
      }
    });

    return { findings, vulnerabilities };
  }

  getAvailableTools(): AI4EHTool[] {
    return this.availableTools;
  }

  async executeFullOrchestration(target: string, goal: string, userId?: string): Promise<OrchestratorSession> {
    // Create session
    const session = await this.createSession(target, goal, userId);
    try {
      console.log(`🚀 Starting full orchestration for target: ${target}, goal: ${goal}`);
      // Use a static, reliable sequence of tools (matching manual tool test)
      const staticSteps: Omit<OrchestratorStep, 'startTime'>[] = [
        {
          id: 'step-1',
          title: 'Subdomain Discovery',
          description: 'Discover subdomains using subfinder',
          status: 'running',
          tool: 'subfinder'
        },
        {
          id: 'step-2',
          title: 'HTTP Probing',
          description: 'Find live web servers using httpx',
          status: 'running',
          tool: 'httpx'
        },
        {
          id: 'step-3',
          title: 'Multi-page Content Scraping',
          description: 'Scrape web content and detect sensitive data',
          status: 'running',
          tool: 'scrape_content'
        },
        {
          id: 'step-4',
          title: 'Keyword Extraction',
          description: 'Extract keywords and analyze content',
          status: 'running',
          tool: 'extract_keywords'
        },
        {
          id: 'step-5',
          title: 'Vulnerability Scanning',
          description: 'Scan for vulnerabilities using nuclei',
          status: 'running',
          tool: 'nuclei'
        },
        {
          id: 'step-6',
          title: 'XSS Testing',
          description: 'Test for XSS vulnerabilities using cai_custom_xss_tool',
          status: 'running',
          tool: 'cai_custom_xss_tool'
        },
        {
          id: 'step-7',
          title: 'Screenshot Analysis',
          description: 'Analyze screenshots using llm_screenshot_classifier',
          status: 'running',
          tool: 'llm_screenshot_classifier'
        },
        {
          id: 'step-8',
          title: 'Directory and File Fuzzing',
          description: 'Fuzz directories and files using ffuf',
          status: 'running',
          tool: 'ffuf_fuzz'
        },
        {
          id: 'step-9',
          title: 'Directory Fuzzing (Dirsearch)',
          description: 'Fuzz directories using dirsearch',
          status: 'running',
          tool: 'dirsearch'
        },
        {
          id: 'step-10',
          title: 'HTTP Request Smuggling Detection',
          description: 'Detect HTTP request smuggling using recollapse',
          status: 'running',
          tool: 'recollapse_fuzz'
        },
        {
          id: 'step-11',
          title: 'Fetch Attack Payloads',
          description: 'Fetch attack payloads from trusted sources',
          status: 'running',
          tool: 'scrape_payloads'
        },
        {
          id: 'step-12',
          title: 'Google Dorking',
          description: 'Google dorking for exposed assets',
          status: 'running',
          tool: 'google_dorking'
        },
        {
          id: 'step-13',
          title: 'GitHub Dorking',
          description: 'GitHub dorking for leaked secrets',
          status: 'running',
          tool: 'github_dorking'
        },
        {
          id: 'step-14',
          title: 'ASN Lookup',
          description: 'ASN and domain information lookup',
          status: 'running',
          tool: 'asn_lookup'
        },
        {
          id: 'step-15',
          title: 'Certificate Transparency Lookup',
          description: 'Certificate transparency lookup using crt.sh',
          status: 'running',
          tool: 'crtsh_lookup'
        },
        {
          id: 'step-16',
          title: 'Censys Mapping',
          description: 'Censys mapping for open ports and services',
          status: 'running',
          tool: 'censys_mapping'
        },
        {
          id: 'step-17',
          title: 'Port Scanning',
          description: 'Scan open ports using naabu',
          status: 'running',
          tool: 'naabu'
        },
        {
          id: 'step-18',
          title: 'Network Scanning',
          description: 'Scan network using nmap',
          status: 'running',
          tool: 'nmap'
        },
        {
          id: 'step-19',
          title: 'Subdomain Enumeration',
          description: 'Enumerate subdomains using amass',
          status: 'running',
          tool: 'amass'
        }
      ];
      let previousOutput: any = null;
      let nucleiFindings: Array<string> = [];
      for (let i = 0; i < staticSteps.length; i++) {
        const step = staticSteps[i];
        console.log(`🔄 Executing step ${i + 1}/${staticSteps.length}: ${step.tool}`);
        try {
          const stepResult = await this.executeStep(session.id, step, previousOutput);
          previousOutput = { output: stepResult.output, metadata: stepResult.metadata };
          // Collect Nuclei findings for enrichment
          if (step.tool === 'nuclei' && stepResult.output) {
            nucleiFindings = Array.isArray(stepResult.output)
              ? stepResult.output
              : stepResult.output.split('\n').filter((l: string) => l.trim());
          }
          // Neural knowledge base enrichment after each step (all tools)
          await updateKnowledgeBaseAPI({
            tool: step.tool,
            output: stepResult.output,
            nucleiFindings: step.tool === 'nuclei' ? nucleiFindings : undefined
          });
          if (i < staticSteps.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error(`❌ Step ${i + 1} failed: ${step.tool}`, error);
          continue;
        }
      }
      // Analyze findings
      const { findings, vulnerabilities } = await this.analyzeFindingsWithGemini(session.id, goal);
      // Query neural knowledge base for enrichment
      const kbEnrichment = await queryKnowledgeBaseAPI(target) as NeuralKBEnrichment;
      await this.updateSession(session.id, { 
        status: 'completed',
        findings: ([...findings, ...(kbEnrichment.suggestions || [])] as any[]),
        vulnerabilities: ([...vulnerabilities, ...(kbEnrichment.cves || [])] as any[])
      });
      return await this.getSession(session.id) || session;
    } catch (error) {
      console.error('❌ Full orchestration failed:', error);
      await this.updateSession(session.id, { status: 'failed' });
      throw error;
    }
  }

  private validateTool(toolName: string): boolean {
    return this.availableTools.some((tool: AI4EHTool) => tool.name === toolName);
  }

  private async analyzeFindingsWithGemini(sessionId: string, goal: string): Promise<{ findings: any[]; vulnerabilities: any[] }> {
    try {
      const session = await this.getSession(sessionId);
      if (!session) {
        throw new Error('Session not found');
      }

      // Collect all step outputs
      const stepOutputs = session.steps
        .filter(step => step.output)
        .map(step => `${step.tool}: ${step.output}`)
        .join('\n\n');

      if (!stepOutputs) {
        return { findings: [] as any[], vulnerabilities: [] as any[] };
      }

      // Call backend API for AI analysis
      const response = await fetch('/api/orchestrator/analyze-findings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          goal,
          stepOutputs
        })
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Analysis failed');
      }

      return {
        findings: result.findings || [],
        vulnerabilities: result.vulnerabilities || []
      };

    } catch (error) {
      console.warn('AI analysis failed, using basic analysis:', error);
      return this.analyzeFindings(sessionId);
    }
  }
}

export const pentestOrchestratorService = new PentestOrchestratorService(); 